import TableHeader from '@/components/table/tableHeader';
import { useResponsive } from '@/hooks/use-responsive';
import styled from 'styled-components';

import { ClientEntityDetailsAllocationsQuery, PaginateSortDirection } from '@/__generated__/graphql';
import { ISelectValue } from '@/components/basicComponents/select';
import { Loader } from '@/components/loaders/loader';
import { StickyTableLoader } from '@/components/loaders/stickyTableLoader';
import { initialValuesFromTable } from '@/components/table/constants';
import FilterPage from '@/components/table/filterPage';
import { TableBodyWrapperWithLoading, TableWrapper } from '@/components/table/styles';
import { useSearchParamsManager } from '@/hooks/use-search-params';
import { NetworkStatus, useQuery } from '@apollo/client';
import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { InvestorDetailsEntityTypeEnum } from '../../../types';
import { allocationsMobileTableSort, allocationsTableColumns } from '../constants';
import EditAmount from '../modals/editAmount';
import {
  BEGIN_ALLOCATION_TAB_GET_INVESTOR_LEGAL_ENTITIES_QUERY,
  BEGIN_ALLOCATION_TAB_GET_LEGAL_ENTITY_BITE_SIZE_QUERY,
  BEGIN_ALLOCATION_TAB_TABLE_DATA_QUERY
} from '../queries';
import AllocationsMobileTable from './mobileTable';
import AllocationsTableBody from './tableBody';

interface AllocationsTableProps {
  entityType: InvestorDetailsEntityTypeEnum;
}

const AllocationsTable = ({ entityType }: AllocationsTableProps) => {
  const params = useParams();
  const navigate = useNavigate();
  const { isMobile, isTablet } = useResponsive();
  const { searchParams, onChangePaginationData, onChangeSearch, onChangeSort, onChangeToggle, makeScroll, saveScrollPostion } =
    useSearchParamsManager();

  const investorId = params?.investorId || '';
  const entityId = params?.entityId || '';
  const q = searchParams.get('q') || '';
  const limit = searchParams.get('limit') || initialValuesFromTable.initialAmountRowsPerPage[0];
  const offset = searchParams.get('offset') || 0;
  const key = searchParams.get('key') || 'name';
  const asc = searchParams.get('asc') === null || searchParams.get('asc') === 'true';
  const toggle = searchParams.get('toggle') === null || searchParams.get('toggle') === 'true';
  const position = searchParams.get('position') || 0;

  const [tableRows, setTableRows] = useState<ClientEntityDetailsAllocationsQuery['ClientEntityDetailsAllocations']['allocations']>([]);
  const [modalWindow, setModalWindow] = useState({ type: '', isOpen: false });
  const [currentRow, setCurrentRow] = useState<ClientEntityDetailsAllocationsQuery['ClientEntityDetailsAllocations']['allocations'][number] | null>(
    null
  );
  const [groupedEntitiesSelectOptions, setGroupedEntitiesSelectOptions] = useState<ISelectValue[]>([]);
  const [biteSize, setBiteSize] = useState({ ignoreBiteSize: false, fundBiteSize: 0, directBiteSize: 0 });
  const [mobileSort, setMobileSort] = useState(allocationsMobileTableSort[0].title);
  const sortItem = allocationsMobileTableSort.find((item) => item.title === mobileSort);
  const mobileSortList = allocationsMobileTableSort.map((item) => item.title);

  const { loading: beginAllocationsTabGetLegalEntityBiteSizeLoading } = useQuery(BEGIN_ALLOCATION_TAB_GET_LEGAL_ENTITY_BITE_SIZE_QUERY, {
    notifyOnNetworkStatusChange: true,
    variables: {
      id: entityId
    },
    onCompleted: (data) => {
      const { ignoreBiteSize, defaultBiteSize1, defaultBiteSize2 } = data.legalEntity || {};
      setBiteSize({ ignoreBiteSize: ignoreBiteSize || false, fundBiteSize: defaultBiteSize1 || 0, directBiteSize: defaultBiteSize2 || 0 });
    }
  });

  const { networkStatus: beginAllocationsTabTableDataNetworkStatus, refetch: beginAllocationsTabTableDataRefetch } = useQuery(
    BEGIN_ALLOCATION_TAB_TABLE_DATA_QUERY,
    {
      notifyOnNetworkStatusChange: true,
      variables: {
        id: entityId,
        includeRecommendation: toggle,
        search: q.toLowerCase().trim(),
        order: asc ? PaginateSortDirection.Asc : PaginateSortDirection.Desc,
        sort: key
      },
      onCompleted: (data) => {
        setTableRows(data.ClientEntityDetailsAllocations.allocations);
        makeScroll(position);
      }
    }
  );

  const { data: beginAllocationsTabGetInvestorLegalEntitiesData, loading: beginAllocationsTabGetInvestorLegalEntitiesLoading } = useQuery(
    BEGIN_ALLOCATION_TAB_GET_INVESTOR_LEGAL_ENTITIES_QUERY,
    {
      notifyOnNetworkStatusChange: true,
      variables: {
        data: {
          id: investorId,
          includeRecommendation: toggle,
          legalEntitiesList: {
            sortBy: 'entityName',
            sortDirection: PaginateSortDirection.Asc
          }
        }
      }
    }
  );

  useEffect(() => {
    if (entityType !== InvestorDetailsEntityTypeEnum.Group) return;
    if (beginAllocationsTabGetInvestorLegalEntitiesLoading || !beginAllocationsTabGetInvestorLegalEntitiesData) return;

    const { legalEntities } = beginAllocationsTabGetInvestorLegalEntitiesData.getInvestor;
    const currentGroup = legalEntities.find((entity) => entity.id === entityId);

    if (currentGroup && currentGroup?.isGroup) {
      const { entities } = currentGroup;

      const selectOptions = entities?.map((entity) => ({
        label: entity.entityName,
        value: entity.id
      }));
      setGroupedEntitiesSelectOptions(selectOptions);
    }
  }, [entityType, beginAllocationsTabGetInvestorLegalEntitiesLoading, beginAllocationsTabGetInvestorLegalEntitiesData]);

  useEffect(() => {
    if (!isMobile) return;

    beginAllocationsTabTableDataRefetch({
      order: sortItem?.asc ? PaginateSortDirection.Asc : PaginateSortDirection.Desc,
      sort: sortItem?.key
    });
  }, [mobileSort]);

  const openModalWindow = (type: string, row: ClientEntityDetailsAllocationsQuery['ClientEntityDetailsAllocations']['allocations'][number]) => {
    setModalWindow({ type, isOpen: true });
    setCurrentRow(row);
  };

  const closeModalWindow = () => {
    setModalWindow({ type: '', isOpen: false });
    setCurrentRow(null);
  };

  const openOpportunityDetailsPage = (strategyId: string) => {
    saveScrollPostion();
    navigate(`/strategyDetails/${strategyId}?backToTitle=Investor Details`);
  };

  const getRowStatusText = (status: string) => {
    switch (status) {
      case 'Outstanding':
        return 'Pending';
      case 'Investment review':
      case 'Advisor review':
      case 'Compliance review':
        return 'Internal Review';
      default:
        return status;
    }
  };

  const isLoading =
    beginAllocationsTabTableDataNetworkStatus === NetworkStatus.loading ||
    beginAllocationsTabTableDataNetworkStatus === NetworkStatus.setVariables ||
    beginAllocationsTabGetInvestorLegalEntitiesLoading ||
    beginAllocationsTabGetLegalEntityBiteSizeLoading;

  return (
    <AllocationsTableWrapper>
      {modalWindow.isOpen && modalWindow.type === 'edit-amount' && (
        <EditAmount
          isOpen={modalWindow.isOpen}
          entityType={entityType}
          row={currentRow}
          biteSize={biteSize}
          groupedEntitiesSelectOptions={groupedEntitiesSelectOptions}
          onClose={closeModalWindow}
        />
      )}
      <FilterPage
        search={{
          value: q,
          onChange: onChangeSearch,
          placeholder: 'Search'
        }}
        resultsValue={tableRows?.length || 0}
        refetch={beginAllocationsTabTableDataRefetch}
        toggle={{
          title: 'Include Pending Commitments',
          isActive: toggle,
          onToggle: () => onChangeToggle(toggle, { shouldSaveScrollPosition: false })
        }}
        mobileSort={{
          value: mobileSort,
          onChange: setMobileSort,
          selects: mobileSortList
        }}
        useNewFilters
      />
      {isMobile ? (
        <>
          {isLoading ? (
            <Loader />
          ) : (
            <AllocationsMobileTable
              tableRows={tableRows}
              openOpportunityDetailsPage={openOpportunityDetailsPage}
              getRowStatusText={getRowStatusText}
            />
          )}
        </>
      ) : (
        <TableWrapper padding="0">
          <TableHeader refetch={() => {}} columns={allocationsTableColumns} savedSort={{ key, asc }} savedSetSort={onChangeSort} />
          {isLoading && <StickyTableLoader />}
          <TableBodyWrapperWithLoading isLoading={isLoading}>
            <AllocationsTableBody
              tableRows={tableRows}
              openModalWindow={openModalWindow}
              openOpportunityDetailsPage={openOpportunityDetailsPage}
              getRowStatusText={getRowStatusText}
            />
          </TableBodyWrapperWithLoading>
        </TableWrapper>
      )}
    </AllocationsTableWrapper>
  );
};

export default AllocationsTable;

const AllocationsTableWrapper = styled.div`
  margin-bottom: 100px;
`;
